/**
 * English (South Africa) — the reference message set. ICU MessageFormat:
 * `{name}` arguments, `{count, plural, one {…} other {…}}` with CLDR plural
 * rules from `Intl.PluralRules`. Every other locale file is typed against
 * this one, so a missing key fails the build, not the visitor.
 *
 * Avoid a bare apostrophe: in ICU it is the quoting character. Use ’.
 */
const en = {
  common: {
    loading: "Loading",
    signIn: "Sign in",
    signOut: "Sign out",
    tryAgain: "Try again",
    back: "Back",
  },
  theme: {
    toLight: "Switch to light theme",
    toDark: "Switch to dark theme",
    light: "Light",
    dark: "Dark",
  },
  locale: {
    label: "Language",
  },
  nav: {
    toggle: "Toggle navigation",
    collapse: "Collapse the sidebar",
    expand: "Expand the sidebar",
    home: "Home",
    /** The way OUT of a production, at the top of the drawer it has replaced. */
    allProductions: "All productions",
    allModules: "All modules",
    /** What the identity block says before the title has arrived — never a uuid. */
    thisProduction: "This production",
    /** The top bar's context selector, when there is nothing to switch between. */
    production: "Production",
    chooseProduction: "Switch production",
    /** D157 — the production's rail while what you are on it is still being asked. */
    loadingProduction: "Loading what is open to you on this production",
    /** D157 — somebody with no standing on the production: said once, quietly, rather than a blank rail. */
    nothingOpen: "Nothing on this production is open to you.",
  },
  /**
   * 🔴 The organisation switcher (D37, D41, D42) — Metaship's own words where it has them, because a
   * screen ported from there reads the same and a person who has used both is not made to relearn it.
   */
  organisation: {
    /** Under the company's name, at the top of the sidebar. Metaship's label, verbatim. */
    change: "Change organisation",
    pick: "Act for",
    search: "Search organisations",
    noMatch: "Nothing by that name",
    /** A person who belongs to no LIVE company. Said plainly, because every scoped screen will refuse. */
    none: "No organisation",
    noneHint: "Ask an administrator to add you to one",
    noneTitle: "You do not belong to a company that is open right now",
    /** D164 — a represented person in no company: her agencies, not an admin prompt. */
    representedHeading: "My work",
    representedBy: "Represented by {agencies}",
    /** What a person IS to a company (D22). The edge's own vocabulary. */
    relationship: {
      principal: "Principal",
      employee: "Employee",
      contractor: "Contractor",
      agent: "Agent",
      booker: "Booker",
    },
    /**
     * Why a company in your list cannot be entered. Written as the state, not as a refusal: the person
     * has done nothing wrong, and "Suspended" tells them who to ask where "Access denied" does not.
     */
    standing: {
      blocked: "Not available",
      SUSPENDED: "Suspended",
      BANNED: "Closed by Pinksy",
      CLOSED: "Closed",
    },
  },
  /**
   * 🔴 The bottom rail (D28, D42) — five things that are true of the whole application rather than of
   * whatever is on screen.
   */
  rail: {
    terms: "Terms and Conditions",
    /** The build's ISO week — the first question of every support conversation. */
    releaseTitle: "The version of Pinksy you are running",
    newVersion: "New version",
    newVersionTitle: "A newer version is ready — reload when it suits you",
    help: "Help",
  },
  preauth: {
    helpAndLegal: "Help and legal",
    support: "Need help?",
    terms: "Terms",
    privacy: "Privacy",
    poweredBy: "Powered by {vendor}",
    landingTitle: "{app} — landing page",
    signingIn: "Signing in…",
  },
  login: {
    email: "E-mail address",
    password: "Password",
    errorGeneric: "Sign-in did not complete. Please try again.",
    forgot: "Forgot your password?",
    passkey: "Sign in with a passkey",
    google: "Continue with Google",
    passkeyHint: "Use your fingerprint, face or device PIN.",
    passkeyFailed: "That passkey was not accepted. You can sign in with your password instead.",
    /** Devin: *"it should at least remember my email address. And say, would you like me to send a reset password to this email address?"* */
    resetOffer: "Would you like us to send a reset link to {email}?",
    resetSend: "Send a reset link",
    resetNeedEmail: "Type your e-mail address above, then ask for a reset link.",
    /**
     * 🔴 Deliberately says IF. The server answers identically for an address it has never
     * seen, so a message promising mail would be a lie half the time and a message saying
     * "no such account" would be the enumeration oracle the endpoint exists to close.
     */
    resetSent: "If that address has an account, a reset link is on its way. It expires in an hour.",
    resetFailed: "Could not reach the server. Please try again.",
  },
  security: {
    title: "How you sign in",
    lede: "You can sign in with a password, a passkey, or a connected Google account. Keep at least one, and you can remove any of the others.",
    passwordTitle: "Password",
    passwordOn: "A password is set.",
    passwordOff: "No password is set.",
    passkeysTitle: "Passkeys",
    googleTitle: "Google",
    googleUnnamed: "Google account",
    usePicture: "Use this picture",
    pictureAdded: "Added to your photos. Choose it as your avatar on your profile.",
    googleConnected: "Connected. You can sign in with this Google account.",
    googleNone: "Not connected.",
    /** Devin: *"I have 2 email address … if I signin with either google SSO I can get in."* */
    googleHint: "Connect any Google account, including one whose address is different from your Pinksy address. After connecting, signing in with it brings you here.",
    connectGoogle: "Connect a Google account",
    remove: "Remove",
    lastMethod: "This is the only way you can sign in. Add another first.",
    removed: "Removed.",
    loadFailed: "Could not read your sign-in methods. Please try again.",
    empty: "You have no passkeys yet. Adding one lets you sign in without your password.",
    unnamed: "Passkey",
    add: "Add a passkey",
    added: "That passkey is ready to use.",
    unsupported: "This browser cannot use passkeys. You can still sign in with your password.",
  },
  reset: {
    title: "Choose a new password",
    intro: "Set a new password for your Pinksy account.",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    mismatch: "Those two passwords do not match.",
    submit: "Set password",
    done: "Your password is set.",
    doneHint: "Every other session has been signed out. You can sign in now.",
    toSignIn: "Go to sign in",
    invalid: "This reset link is not valid any more. Ask for a new one from the sign-in screen.",
  },
  invitation: {
    title: "You have been invited to {org}",
    titleBare: "You have been invited",
    loading: "Opening your invitation…",
    invalid: "This invitation is not valid any more. Ask whoever invited you to send another.",
    chooseIntro: "Choose a password for {email} to accept and get started.",
    newPassword: "Choose a password",
    confirmPassword: "Confirm password",
    mismatch: "Those two passwords do not match.",
    claim: "Accept and set password",
    claimed: "You are in.",
    claimedHint: "Your password is set and you are now part of {org}. Sign in to get started.",
    signInIntro: "You already have a Pinksy account as {email}. Sign in as yourself to accept.",
    signIn: "Sign in to accept",
    accepting: "Accepting your invitation…",
    accepted: "Welcome to {org}.",
    acceptedHint: "The invitation is accepted. You can carry on.",
    carryOn: "Carry on",
    toSignIn: "Go to sign in",
    onboardingTitle: "Complete your details for the production",
    onboardingChooseIntro: "Choose a password for {email}. Then sign in and complete the details the production asked for — about ten minutes on your phone.",
    onboardingSignInIntro: "You already have a Pinksy account as {email}. Sign in and your details are waiting.",
    onboardingClaimedHint: "Your password is set. Sign in to complete your details.",
    onboardingSignIn: "Sign in",
    onboardingOpen: "Open my details",
    onboardingReady: "Your details are waiting on your profile.",
  },
  onboardStart: {
    title: "Your onboarding for {production}",
    titleBare: "Start your onboarding",
    loading: "Opening the link…",
    intro: "The production asked you to complete your details on Pinksy. We email you your own link — to {email}.",
    send: "Email me my onboarding link",
    sent: "Sent. Check your email at {email} — the link is in a message from Pinksy. It may take a minute, and can land in spam.",
    mailOff: "Email is not switched on yet — ask the production office.",
    throttled: "We emailed you a moment ago — check your inbox and spam folder. You can ask again in a few minutes.",
    notDelivered: "The email could not be sent just now. Try again in a little while, or ask the production office.",
    unavailable: "This link cannot send your onboarding just now. Ask the production office.",
    invalid: "This link is not valid any more. Ask the production office for a new one.",
    invalidHint: "Onboarding links last a few weeks. The production office can send you a new one.",
    unreachable: "Pinksy could not be reached just now. Check your signal and try again.",
    unreachableHint: "Your link is probably fine — this is usually a weak signal or a busy moment.",
    retry: "Try again",
    signIn: "I already have a Pinksy account — sign in",
    footnote: "This page never shows your details. The email goes only to the address the production has for you.",
  },
  noAccess: {
    title: "No access to {app}",
    text: "You are signed in, but this account is not part of this app. Ask an administrator to add you.",
    signedInAs: "Signed in as",
    switchAccount: "Sign in with a different account",
    contactSupport: "Contact support",
  },
  appHome: {
    greeting: "Welcome back, {name}",
    empty: "No apps are available yet.",
    emptyHint: "An administrator can add modules to {app}.",
  },
  footer: {
    language: "Language",
    appearance: "Appearance",
    organisation: "Organisation",
    signedIn: "Signed in",
    myProfile: "My profile",
    installApp: "Install app",
    greetingNight: "Burning the midnight oil",
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    greetingLate: "Working late",
  },
};

export type ShellMessages = typeof en;

export default en;
